// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// game.ts — a played game: the bag, the rounds it deals, the ply log that produced the position, and
// the resolution.
//
// THE LOG IS THE GAME. Everything except the bag is public, and the bag is a pure function of the
// shared seed, so the whole state is a pure function of (mode, players, seed, plies). That is what
// makes the multiplayer shape possible: a peer sends its whole log in every message, a dropped
// datagram is unobservable, a late joiner catches up from one message, and a peer running a
// different build is DETECTED — its log fails to replay through our own generator — rather than
// silently diverging.
//
// The bag is COUNTS plus a seeded weighted draw, never a shuffled array. Two peers cannot disagree
// about the order of a multiset they never ordered, so the reflood (discard back into the bag when
// it runs dry mid-deal) is canonical by construction.

import { makeRng, type Rng } from '@ben-gy/game-engine/rng';
import { KINDS, RACK_SIZE, TILES_PER_KIND, TOTAL_TILES } from './kinds';
import { modeOf, type Mode, type ModeId } from './modes';
import {
  SILL,
  Table,
  apply,
  finalScore,
  heldTiles,
  isLegal,
  legalMoves,
  moveOf,
  plyOf,
  ranking,
  resolveRound,
  type LayEvent,
  type Move,
  type PenaltyEvent,
} from './rules';

/** Bump when a rule changes shape, so two builds partition loudly instead of diverging quietly. */
export const RULES_REV = 1;

export interface DealEvent {
  readonly round: number;
  /** rackCount * KINDS counts, exactly as dealt. The audit replays from this, not from our bag. */
  readonly racks: readonly number[];
  /** How many racks got a short (or empty) fill because the bag and discard were both exhausted. */
  readonly short: number;
}

export interface Standing {
  readonly seat: number;
  readonly score: number;
  readonly bonus: number;
  readonly rows: number;
  readonly cols: number;
  readonly kinds: number;
  readonly spoiled: number;
  readonly laid: number;
  /** Tiles still sitting in unfinished courses when the game ended — what you never got to use. */
  readonly stranded: number;
}

export class Game {
  readonly mode: Mode;
  readonly players: number;
  readonly seed: number;
  readonly table: Table;
  readonly plies: number[] = [];
  readonly deals: DealEvent[] = [];

  private readonly rng: Rng;
  private readonly bag = new Int16Array(KINDS);
  private readonly discard = new Int16Array(KINDS);
  /** Tiles each seat has ever sent to the Spoil. Tie-break, and the sim's "has the ladder teeth". */
  private readonly spoiled: number[];
  private readonly laid: number[];

  constructor(mode: Mode | ModeId | string, players: number, seed: number) {
    this.mode = typeof mode === 'string' ? modeOf(mode) : mode;
    this.players = Math.max(2, Math.min(4, players | 0));
    this.seed = seed >>> 0;
    this.rng = makeRng(this.seed);
    this.table = new Table(this.mode, this.players);
    this.spoiled = new Array(this.players).fill(0);
    this.laid = new Array(this.players).fill(0);
    for (let k = 0; k < KINDS; k++) this.bag[k] = TILES_PER_KIND;
    this.table.bagCount = TOTAL_TILES;
    this.deal();
  }

  get over(): boolean {
    return this.table.over;
  }
  get round(): number {
    return this.table.round;
  }
  get seat(): number {
    return this.table.seat;
  }

  legal(): Move[] {
    return legalMoves(this.table);
  }

  /** One tile out of the bag, reflooding from the discard if it has run dry. -1 if nothing is left. */
  private draw(): number {
    let total = 0;
    for (let k = 0; k < KINDS; k++) total += this.bag[k];
    if (total === 0) {
      let d = 0;
      for (let k = 0; k < KINDS; k++) d += this.discard[k];
      if (d === 0) return -1;
      for (let k = 0; k < KINDS; k++) {
        this.bag[k] = this.discard[k];
        this.discard[k] = 0;
      }
      total = d;
    }
    let r = Math.floor(this.rng() * total);
    for (let k = 0; k < KINDS; k++) {
      r -= this.bag[k];
      if (r < 0) {
        this.bag[k]--;
        return k;
      }
    }
    // Only reachable if rng() ever returned exactly 1. Take the last non-empty kind.
    for (let k = KINDS - 1; k >= 0; k--) {
      if (this.bag[k] > 0) {
        this.bag[k]--;
        return k;
      }
    }
    return -1;
  }

  /**
   * Fill the racks in index order. When the bag and the discard are BOTH exhausted the remaining
   * racks simply stay empty and the round is played short — the round-end condition already handles
   * an empty rack. If nothing at all can be dealt the game is over and is scored where it stands.
   */
  private deal(): void {
    const t = this.table;
    t.racks.fill(0);
    t.sill.fill(0);
    t.trowelInSill = true;
    let dealt = 0;
    let short = 0;
    for (let r = 0; r < t.rackCount; r++) {
      let got = 0;
      for (let i = 0; i < RACK_SIZE; i++) {
        const k = this.draw();
        if (k < 0) break;
        t.racks[r * KINDS + k]++;
        got++;
      }
      dealt += got;
      if (got < RACK_SIZE) short++;
    }
    this.deals.push({ round: t.round, racks: Array.from(t.racks), short });
    this.refreshBagCount();
    if (dealt === 0) {
      t.over = true;
      t.endReason = 'drained';
    }
  }

  private refreshBagCount(): void {
    let n = 0;
    for (let k = 0; k < KINDS; k++) n += this.bag[k] + this.discard[k];
    this.table.bagCount = n;
  }

  /** Play a move. Returns null if it is not legal here — which is also how a replay detects that the
   *  sender is running different code. */
  play(m: Move): {
    lays: readonly LayEvent[];
    penalties: readonly PenaltyEvent[];
    roundEnded: boolean;
  } | null {
    const t = this.table;
    if (t.over || !isLegal(t, m)) return null;
    const seat = t.seat;
    const res = apply(t, m);
    this.plies.push(plyOf(m));
    this.discard[res.kind] += res.discarded;
    this.spoilKinds[res.kind] += res.overflow;
    this.spoiled[seat] += res.overflow;
    for (const l of res.lays) this.laid[l.seat]++;

    let lays: readonly LayEvent[] = res.lays;
    let penalties: readonly PenaltyEvent[] = [];
    let roundEnded = false;

    if (t.over) {
      // Quickfire's instant end: penalties are still taken exactly once, right now.
      const r = resolveRound(t);
      penalties = r.penalties;
      this.sweep(r.discarded);
      roundEnded = true;
    } else if (t.roundOver()) {
      const r = resolveRound(t);
      lays = [...lays, ...r.lays];
      penalties = r.penalties;
      for (const l of r.lays) this.laid[l.seat]++;
      this.sweep(r.discarded);
      roundEnded = true;
      if (!t.over) {
        if (t.round >= this.mode.roundCap) {
          t.over = true;
          t.endReason = 'cap';
        } else {
          t.round++;
          t.seat = t.lead;
          this.deal();
        }
      }
    }
    return { lays, penalties, roundEnded };
  }

  /**
   * The round-end sweep. Course leftovers AND every tile on every Spoil go back to the discard, and
   * the Spoil tracks are emptied. Without this the ladder would be cumulative for the whole game and
   * tiles would leave circulation for good — which, with a short deal, assembles into a game that
   * deals nothing and loops empty rounds forever.
   */
  private readonly spoilKinds = new Int16Array(KINDS);
  private sweep(courseDiscards: readonly number[]): void {
    for (let k = 0; k < KINDS; k++) {
      this.discard[k] += (courseDiscards[k] ?? 0) + this.spoilKinds[k];
      this.spoilKinds[k] = 0;
    }
    this.refreshBagCount();
  }

  playPly(ply: number): boolean {
    const m = moveOf(ply);
    return m !== null && this.play(m) !== null;
  }

  /** Rebuild a game from a log. Null if any ply is illegal — a peer on a different build fails
   *  loudly here instead of diverging in silence. */
  static replay(
    mode: Mode | ModeId | string,
    players: number,
    seed: number,
    plies: readonly number[],
  ): Game | null {
    const g = new Game(mode, players, seed);
    for (const ply of plies) {
      if (g.over) return null;
      if (!g.playPly(ply)) return null;
    }
    return g;
  }

  /**
   * What game this is, as one number: the rulebook revision, the mode, the table size and the seed.
   *
   * It goes on the wire beside the ply log because replaying a log is only a PROBABILISTIC mismatch
   * detector — a short log of moves that happen to be legal under both rulebooks replays perfectly,
   * and two peers then diverge in silence a few plies later. The digest makes a mismatch fail loudly
   * on the very first message instead. Bump RULES_REV whenever a rule changes shape.
   */
  digest(): number {
    let h = 0x811c9dc5 ^ RULES_REV;
    const mix = (x: number): void => {
      h = Math.imul(h ^ (x & 0xff), 0x01000193);
      h = Math.imul(h ^ ((x >>> 8) & 0xff), 0x01000193);
      h = Math.imul(h ^ ((x >>> 16) & 0xff), 0x01000193);
      h = Math.imul(h ^ ((x >>> 24) & 0xff), 0x01000193);
    };
    for (let i = 0; i < this.mode.id.length; i++) mix(this.mode.id.charCodeAt(i));
    mix(this.players);
    mix(this.seed);
    return h >>> 0;
  }

  /** End the game because everyone else left. Recorded as a resolution, never as a ply. */
  walkover(): void {
    if (this.table.over) return;
    this.table.over = true;
    this.table.endReason = 'row';
  }

  ranking(): number[] {
    return ranking(this.table, this.spoiled);
  }

  standings(): Standing[] {
    const t = this.table;
    const out: Standing[] = [];
    for (let s = 0; s < this.players; s++) {
      let rows = 0;
      let cols = 0;
      for (let i = 0; i < 5; i++) {
        if (t.rowBits[s * 5 + i] === 0b11111) rows++;
        if (t.colBits[s * 5 + i] === 0b11111) cols++;
      }
      const counts = new Int8Array(KINDS);
      for (let i = 0; i < 25; i++) {
        const k = t.mosaic[s * 25 + i];
        if (k >= 0) counts[k]++;
      }
      let kinds = 0;
      for (let k = 0; k < KINDS; k++) if (counts[k] === 5) kinds++;
      let stranded = 0;
      for (let c = 0; c < 5; c++) stranded += t.courseCount[s * 5 + c];
      out.push({
        seat: s,
        score: finalScore(t, s),
        bonus: finalScore(t, s) - Math.max(0, t.score[s]),
        rows,
        cols,
        kinds,
        spoiled: this.spoiled[s],
        laid: this.laid[s],
        stranded,
      });
    }
    return out;
  }

  /** Tiles in every bucket. The conservation invariant the audit checks at every step. */
  census(): { bag: number; table: number; boards: number; discard: number; total: number } {
    const t = this.table;
    let bag = 0;
    let disc = 0;
    for (let k = 0; k < KINDS; k++) {
      bag += this.bag[k];
      disc += this.discard[k];
    }
    let boards = 0;
    for (let s = 0; s < this.players; s++) {
      boards += heldTiles(t, s);
      for (let i = 0; i < 25; i++) if (t.mosaic[s * 25 + i] >= 0) boards++;
    }
    const table = t.tableTiles();
    return { bag, table, boards, discard: disc, total: bag + table + boards + disc };
  }
}

export { SILL };
